import React from 'react';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';

// Geltonas taškas (be apskritimo kontūro) laukiantiems pasirinkimams; standartinė
// žalia varnelė (be apskritimo fono) jau atliktiems — žr. 16 punktų sąrašo #12.
const PENDING_BADGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='24' viewBox='0 0 24 24' width='24'%3E%3Ccircle cx='12' cy='12' r='6' fill='%23FFD700'/%3E%3C/svg%3E";
const ACTIVE_BADGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='24' viewBox='0 0 24 24' width='24'%3E%3Cpath fill='%2309aa89' d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z'/%3E%3C/svg%3E";

const useStyles = makeStyles({
    box: {
        position: 'relative',
        borderRadius: tokens.borderRadiusMedium,
        cursor: 'pointer',
        transitionProperty: 'background, border-color',
        transitionDuration: '0.15s',
        transitionTimingFunction: 'ease',
        boxSizing: 'border-box',
        backgroundColor: '#fff',
        // SVARBU: `borderColor`/`borderWidth`/`borderStyle` (be krypties) yra
        // Griffel'io UNSUPPORTED_CSS_PROPERTIES sąraše
        // (@griffel/core/src/constants.js) — Griffel jas TYLIAI IŠMETA (tik
        // console.error, jokios CSS taisyklės nesugeneruojama), tad anksčiau
        // čia (klaidingai) naudotos "ilgosios" savybės apskritai NEVEIKĖ —
        // border visiškai dingdavo VISOMS kortelėms, ne tik hover/active
        // spalva neveikdavo (žr. /admin/bugs "Dingo border, reikia grazinti
        // su zaliu hover"). Bazinis "border" shorthand YRA palaikomas
        // (Griffel jį išplečia į visas 12 krypčių savybių), o hover/active
        // perrašymui reikia VISŲ 4 kryptinių "borderXColor" (tikros,
        // palaikomos ilgosios savybės) — patikrinta gyvai, kad šis derinys
        // patikimai nustelbia bazinį border'į.
        border: '1px solid #e0e0e0',
        minHeight: '140px',
        ':hover': {
            backgroundColor: '#ffffff',
            borderTopColor: '#09aa89', borderRightColor: '#09aa89',
            borderBottomColor: '#09aa89', borderLeftColor: '#09aa89',
        },
    },
    boxDisabled: { cursor: 'not-allowed', backgroundColor: '#f0f0f0', opacity: 0.6 },
    // Fonas paliktas baltas — vien žalias border + varnelė pakanka "pasirinkta"
    // būsenai parodyti, žalias fonas atrodė perteklinis (žr. /admin/bugs "Kai
    // sis elementas yra active, nereikia jam background color zalios").
    boxActive: {
        borderTopColor: '#09aa89', borderRightColor: '#09aa89',
        borderBottomColor: '#09aa89', borderLeftColor: '#09aa89',
    },
    // Standartinis dydis visur: plotis = paveikslėlio plotis (60px, žr. imageWrap)
    // + 10px paddingas iš šonų — NE 100% grid langelio pločio (anksčiau mygtukai
    // ištempdavosi iki jiems skirto grid stulpelio pločio, tad tas pats mygtukas
    // skirtinguose dialoguose būdavo skirtingo dydžio, priklausomai nuo to dialogo
    // grid apibrėžimo). justifySelf:'start' neleidžia CSS Grid ištempti elemento
    // per visą stulpelį, kai jis naudojamas grid konteineryje.
    // Padidinta 80px→96px (žr. /admin/bugs "pacius mygtukus reiktu padidinti").
    boxColumn: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        width: '96px', justifySelf: 'start',
        paddingLeft: '12px', paddingRight: '12px',
        paddingTop: tokens.spacingVerticalM, paddingBottom: tokens.spacingVerticalM,
        textAlign: 'center',
    },
    // Didesnis variantas (žr. /admin/bugs "turi buti maziau item eiluteje ~6-8")
    // — opt-in per `large` props, kad nepaveiktų kitų dialogų, kurie naudoja
    // standartinį 96px dydį. Buvęs "compact" (mažesnis, ne didesnis) variantas čia
    // anksčiau spręsdavo PRIEŠINGĄ prašymą tam pačiam plokščių pasirinkimo dialogui
    // (žr. git istoriją) — vartotojo skonis pasikeitė, tad pakeista, ne pridėta šalia.
    boxColumnLarge: {
        width: '140px', paddingLeft: '18px', paddingRight: '18px',
        paddingTop: tokens.spacingVerticalM, paddingBottom: tokens.spacingVerticalM,
    },
    boxInline: {
        display: 'flex', flexDirection: 'row', alignItems: 'center',
        padding: tokens.spacingHorizontalM, width: '100%', minHeight: 'auto',
        border: 'none !important',
    },
    // right/top atitolinta 5px→10px nuo krašto (žr. /admin/bugs "Sie icons turi
    // buti truputi labiau atitole nuo virsaus ir desines puses").
    badge: {
        content: '""', position: 'absolute', right: '10px', top: '10px',
        width: '15px', height: '15px', backgroundSize: 'contain', zIndex: 9,
    },
    badgePending: { backgroundImage: `url("${PENDING_BADGE}")` },
    badgeActive: { backgroundImage: `url("${ACTIVE_BADGE}")` },

    imageWrap: {
        overflow: 'hidden', position: 'relative',
        width: '72px', height: '72px', borderRadius: tokens.borderRadiusMedium, flexShrink: 0,
    },
    imageWrapLarge: { width: '104px', height: '104px' },
    imageWrapColumn: { marginBottom: tokens.spacingHorizontalM },
    imageWrapInline: { marginRight: '15px', borderRadius: `${tokens.borderRadiusMedium} !important` },
    imageImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
    placeholder: {
        width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#f0f0f0', position: 'absolute', top: 0, left: 0,
    },
    placeholderHidden: { display: 'none' },
    placeholderVisible: { display: 'flex' },
    placeholderIcon: { fontSize: tokens.fontSizeBase600, color: '#ccc' },

    icon: { marginRight: '15px' },

    content: { flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' },
    contentColumn: { width: '100%' },
    contentInline: { textAlign: 'left' },

    // lineHeight sumažintas — numatytasis (paveldėtas) buvo per didelis šiam
    // mažam šriftui, tad kelių eilučių pavadinimai atrodė "laisvi" (žr.
    // /admin/bugs "Line height yra per didelis teksto").
    title: { fontSize: tokens.fontSizeBase200, lineHeight: '1.25' },
    titleDisabled: { color: '#999' },
    titleActive: { color: '#09aa89' },

    subtitle: { fontSize: '11px', marginTop: tokens.spacingVerticalXXS, fontWeight: tokens.fontWeightSemibold },
    subtitleDisabled: { color: '#aaa' },

    footer: {
        borderTop: '1px solid #eee', marginTop: tokens.spacingVerticalS, paddingTop: tokens.spacingVerticalS,
        fontSize: '0.8rem', fontWeight: tokens.fontWeightBold, textAlign: 'center', color: '#555', width: '100%',
    },
    footerDisabled: { color: '#999' },

    hoverOverlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.7)', textAlign: 'center', borderRadius: tokens.borderRadiusMedium,
        display: 'flex', position: 'absolute', top: '2px', left: '2px', right: '2px', bottom: '2px',
        flexDirection: 'column', alignItems: 'center', alignContent: 'center',
        transitionProperty: 'all', transitionDuration: '0.1s', transitionTimingFunction: 'ease-in-out',
        opacity: 0, visibility: 'hidden',
        '& > div': {
            display: 'flex', borderRadius: 0, width: '100%', height: '50%', color: '#fff',
            fontSize: '11px', justifyContent: 'center', alignItems: 'center', cursor: 'pointer',
        },
        '& > div:hover:not(.disabled-link)': { textDecorationLine: 'underline' },
        '& > div:first-child': { borderBottom: '2px solid rgba(255, 255, 255, 1)' },
        '& > div.disabled-link': { color: '#ccc', cursor: 'not-allowed' },
    },
    hoverOverlayVisible: { opacity: 1, visibility: 'visible' },
});

const SelectionBox = ({
    image,
    title,
    subTitle,
    selected,
    onClick,
    linkOne,
    linkTwo,
    linkOneLabel = 'Žiūrėti',
    linkTwoLabel = 'Pasirinkti',
    footer,
    disabled = false,
    isIcon,
    icon,
    type = 'column',
    isPending = false, // NAUJAS PROPS: nurodo, ar elementas yra "pending" būsenos
    large = false,
}) => {
    const s = useStyles();
    const isInline = type === 'inline';
    const [hovered, setHovered] = React.useState(false);

    const handleLinkClick = (e, linkFunction) => {
        e.stopPropagation();
        if (linkFunction && !disabled) {
            linkFunction();
        }
    };

    const handleClick = (e) => {
        if (!disabled && onClick) {
            onClick(e);
        }
    }

    return (
        <div
            onClick={handleClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className={mergeClasses(
                s.box,
                isInline ? s.boxInline : s.boxColumn,
                !isInline && large && s.boxColumnLarge,
                disabled && s.boxDisabled,
                !disabled && selected && s.boxActive,
            )}
        >
            {!disabled && (selected || isPending) && (
                <div className={mergeClasses(s.badge, selected ? s.badgeActive : s.badgePending)} />
            )}

            {/* Paveiksliukas */}
            {!isIcon && (
                <div className={mergeClasses(s.imageWrap, isInline ? s.imageWrapInline : s.imageWrapColumn, !isInline && large && s.imageWrapLarge)}>
                    {image
                        ? <img src={image} alt="" onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                            className={s.imageImg} />
                        : null}
                    <div className={mergeClasses(s.placeholder, image ? s.placeholderHidden : s.placeholderVisible)}>
                        <span className={mergeClasses('material-symbols-outlined', s.placeholderIcon)}>image</span>
                    </div>
                </div>
            )}

            {isIcon && (
                <div className={s.icon}>
                    <span className="material-symbols-outlined" aria-hidden="true">{icon}</span>
                </div>
            )}

            <div className={mergeClasses(s.content, isInline ? s.contentInline : s.contentColumn)}>
                {/* Pavadinimas */}
                <div className={mergeClasses(s.title, disabled && s.titleDisabled, !disabled && selected && s.titleActive)}>{title}</div>

                {/* Paantraštė */}
                {subTitle && <div className={mergeClasses(s.subtitle, disabled && s.subtitleDisabled)}>{subTitle}</div>}

                {/* Footer dalis (kainai) */}
                {footer && (
                    <div className={mergeClasses(s.footer, disabled && s.footerDisabled)}>
                        {footer}
                    </div>
                )}
            </div>

            {/* Hover actions */}
            {(linkOne || linkTwo) && (
                <div className={mergeClasses(s.hoverOverlay, hovered && !disabled && s.hoverOverlayVisible)}>
                    {linkOne && (
                        <div
                            onClick={(e) => handleLinkClick(e, linkOne)}
                            className={disabled ? 'disabled-link' : ''}
                        >
                            {linkOneLabel}
                        </div>
                    )}

                    {linkTwo && (
                        <div
                            onClick={(e) => handleLinkClick(e, linkTwo)}
                            className={disabled ? 'disabled-link' : ''}
                        >
                            {linkTwoLabel}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SelectionBox;
